<template>
  <BaseModal :modalTitle="$t('area.analysis-test.create.title')">
    <ModalStepGuide
      :stepTitles="[$t('W: Detaljer'), $t('W: Klassificering')]"
      :currentStep="currentStep" />

    <!-- Step 1: Details -->
    <UiBaseFormVee
      v-if="currentStep === 1"
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onStep1Next">
      <UiInputTextFieldVee
        name="name"
        :label="$t('W: Internt navn')"
        :placeholder="$t('form.placeholder.name')"
        :hint="$t('W: Navnet der bruges internt af laboratoriet, f.eks. i lister og opsætning.')"
        :unit="currentLocale.toUpperCase()"
        :rules="['required']"
        isRequired
        v-model="form.name" />

      <div class="mt-4">
        <UiInputTextFieldVee
          name="displayName"
          :label="$t('W: Vist navn')"
          :placeholder="$t('form.placeholder.name')"
          :hint="$t('W: Navnet kunden ser i stedet for det interne navn.')"
          :unit="currentLocale.toUpperCase()"
          :rules="['required']"
          isRequired
          v-model="form.displayName" />
      </div>

      <div class="mt-4">
        <UiInputSelectVee
          name="type"
          :label="$t('term.type')"
          :options="typeOptions"
          :rules="['required']"
          isRequired
          v-model="form.type" />
      </div>

      <template v-if="form.type === 'prove-based'">
        <div class="mt-4">
          <UiComboboxVee
            name="provedTextID"
            :label="$t('W: Label ved ja')"
            :options="provedTextOptions"
            :locale="currentLocale"
            allowCreate
            v-model="form.provedTextID"
            @on-pending-create="onPendingCreateProvedText" />
        </div>

        <div class="mt-4">
          <UiComboboxVee
            name="notProvedTextID"
            :label="$t('W: Label ved nej')"
            :options="notProvedTextOptions"
            :locale="currentLocale"
            allowCreate
            v-model="form.notProvedTextID"
            @on-pending-create="onPendingCreateNotProvedText" />
        </div>

        <div class="mt-4">
          <UiInputSelectVee
            name="qaTrigger"
            :label="$t('W: QA-godkendelse kræves')"
            :options="qaTriggerOptions"
            v-model="form.qaTrigger" />
        </div>
      </template>

      <div
        v-if="form.type === 'value-based'"
        class="mt-4 space-y-4">
        <UiComboboxVee
          name="unitID"
          :label="$t('term.unit')"
          :options="unitOptions"
          :locale="currentLocale"
          allowCreate
          v-model="form.unitID"
          @on-pending-create="onPendingCreateUnit" />

        <span class="mb-1 block font-medium">
          {{ $t('area.analysis-test.field.measurement-limits') }}
        </span>

        <div class="flex items-center gap-2">
          <AppSwitch
            :isActive="form.hasTooLowToMeasure"
            :title="$t('area.analysis-test.message.too-low-to-measure')"
            @on-toggle="form.hasTooLowToMeasure = !form.hasTooLowToMeasure" />
          <span class="text-sm text-text-primary">
            {{ $t('area.analysis-test.message.too-low-to-measure') }}
          </span>
        </div>
        <UiInputNumberFieldVee
          v-if="form.hasTooLowToMeasure"
          name="minLimit"
          :label="$t('area.analysis-test.field.min-limit')"
          :decimals="2"
          :unit="selectedUnitText"
          isRequired
          showAsRequired
          v-model="form.minLimit" />

        <div class="flex items-center gap-2">
          <AppSwitch
            :isActive="form.hasTooHighToMeasure"
            :title="$t('area.analysis-test.message.too-high-to-measure')"
            @on-toggle="form.hasTooHighToMeasure = !form.hasTooHighToMeasure" />
          <span class="text-sm text-text-primary">
            {{ $t('area.analysis-test.message.too-high-to-measure') }}
          </span>
        </div>
        <UiInputNumberFieldVee
          v-if="form.hasTooHighToMeasure"
          name="maxLimit"
          :label="$t('area.analysis-test.field.max-limit')"
          :decimals="2"
          :unit="selectedUnitText"
          isRequired
          showAsRequired
          v-model="form.maxLimit" />
      </div>

      <div
        v-if="form.type === 'single-select'"
        class="mt-4 space-y-4">
        <AnalysisTestAnswersSection v-model="form.answers" />
        <AnalysisTestQaSetting
          :answers="form.answers"
          v-model:qaMode="form.qaMode"
          v-model:qaAnswerIDs="form.qaAnswerIDs" />
      </div>

      <div
        v-if="form.type === 'multi-select-text'"
        class="mt-4 space-y-4">
        <UiComboboxMultiVee
          name="reportDescriptionIDs"
          :label="$t('area.analysis-test.field.text-list')"
          :placeholder="$t('W: Vælg lister og beskrivelser')"
          :options="reportDescriptionOptions"
          :rules="['required']"
          isRequired
          v-model="form.reportDescriptionIDs" />

        <UiComboboxMultiVee
          name="suffixReportDescriptionIDs"
          :label="$t('W: Suffikser')"
          :placeholder="$t('W: Vælg lister med suffikser')"
          :options="suffixReportDescriptionOptions"
          v-model="form.suffixReportDescriptionIDs" />

        <UiInputRadioGroupVee
          name="answerMode"
          :label="$t('W: Svarindstilling')"
          direction="horizontal"
          :options="answerModeOptions"
          v-model="form.answerMode" />

        <AnalysisTestAnswersSection
          v-if="form.answerMode !== 'none'"
          v-model="form.answers" />
      </div>

      <div
        v-if="form.type === 'multi-select-number'"
        class="mt-4 space-y-4">
        <UiComboboxMultiVee
          name="reportDescriptionIDs"
          :label="$t('area.analysis-test.field.text-list')"
          :placeholder="$t('W: Vælg lister og beskrivelser')"
          :options="reportDescriptionOptions"
          :rules="['required']"
          isRequired
          v-model="form.reportDescriptionIDs" />

        <UiComboboxMultiVee
          name="suffixReportDescriptionIDs"
          :label="$t('W: Suffikser')"
          :placeholder="$t('W: Vælg lister med suffikser')"
          :options="suffixReportDescriptionOptions"
          v-model="form.suffixReportDescriptionIDs" />

        <UiComboboxVee
          name="unitID"
          :label="$t('term.unit')"
          :options="unitOptions"
          :locale="currentLocale"
          allowCreate
          v-model="form.unitID"
          @on-pending-create="onPendingCreateUnit" />

        <AnalysisTestAnswersSection
          v-model="form.answerOverrides"
          :description="$t('area.analysis-test.message.override-hint')" />

        <div class="flex items-center gap-2">
          <AppSwitch
            :isActive="form.hasSumValue"
            :title="$t('W: Tilføj sum-værdi')"
            @on-toggle="form.hasSumValue = !form.hasSumValue" />
          <span class="text-sm text-text-primary">
            {{ $t('W: Tilføj sum-værdi') }}
          </span>
        </div>
      </div>

      <!-- Applies to every test type, so it sits after the type-specific
           sections rather than inside one of them. -->
      <span class="mb-2 mt-6 block font-medium">
        {{ $t('W: Indstillinger') }}
      </span>
      <div class="flex items-center gap-2">
        <AppSwitch
          :isActive="form.isOptional"
          :title="$t('W: Valgfri test')"
          @on-toggle="form.isOptional = !form.isOptional" />
        <span class="text-sm text-text-primary">
          {{ $t('W: Valgfri test') }}
        </span>
      </div>
      <p class="mt-1 text-xs text-text-muted">
        {{ $t('W: Laboranten kan slå testen fra ved resultatindtastning, så den udelades af rapporten') }}
      </p>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.next')"
          type="submit" />
      </template>
    </UiBaseFormVee>

    <!-- Step 2: Klassificering -->
    <div v-else-if="currentStep === 2">
      <template
        v-if="
          form.type === 'prove-based' ||
          form.type === 'value-based' ||
          form.type === 'single-select'
        ">
        <div class="mb-4 flex items-center gap-2">
          <AppSwitch
            :isActive="form.colorConfigEnabled"
            :title="$t('W: Aktiver klassificering')"
            @on-toggle="form.colorConfigEnabled = !form.colorConfigEnabled" />
          <span>{{ $t('W: Aktiver klassificering') }}</span>
        </div>

        <template v-if="form.colorConfigEnabled">
          <div
            v-if="form.type === 'prove-based'"
            class="space-y-6">
            <div class="space-y-3">
              <span class="block text-sm font-medium text-text-primary">
                {{ notProvedLabel }}
              </span>
              <ColorPickerButton v-model="form.notProvedColorID" />
            </div>
            <div class="space-y-3">
              <span class="block text-sm font-medium text-text-primary">
                {{ provedLabel }}
              </span>
              <ColorPickerButton v-model="form.provedColorID" />
            </div>
          </div>

          <AnalysisTestAnswerColors
            v-else-if="form.type === 'single-select'"
            :answers="form.answers"
            v-model="form.answerColorIDs" />

          <AnalysisTestConditionsList
            v-else
            v-model="form.conditions" />
        </template>
      </template>
      <p
        v-else
        class="text-sm text-text-muted italic">
        {{ $t('W: Klassificering er ikke relevant for denne testtype') }}
      </p>
    </div>

    <template
      v-if="currentStep === 2"
      #buttons>
      <UiBaseButton
        :text="$t('action.back')"
        :isDisabled="isLoading"
        @button-click="prevStep" />
      <UiBaseButton
        :text="$t('action.create')"
        :isLoading="isLoading"
        :isDisabled="hasValidationErrors"
        @button-click="onCreate" />
    </template>
  </BaseModal>
</template>

<script lang="ts">
import { useAnalysisTestStore } from '@/stores/analysisTestStore'
import { useModalStore } from '@/stores/modalStore'
import { useTextListStore } from '@/stores/textListStore'
import { useReportDescriptionStore } from '@/stores/reportDescriptionStore'
import { useAppStore } from '@/stores/appStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { mapActions, mapState } from 'pinia'
import { generateFirestoreId } from '@/globals/typescript/utils/utils'
import {
  buildEmptyAnalysisTestFocusForm,
  buildAnalysisTestCreatePayload,
  computeValueBasedConditionErrors,
  isSingleSelectQaIncomplete,
} from '@/globals/typescript/utils/analysisTestFocusForm'
import type { TAnalysisTestFocusForm } from '@/globals/typescript/utils/analysisTestFocusForm'
import { buildResultTextItems } from '@/globals/typescript/utils/resultTextOptions'
import type { TResultTextOption } from '@/globals/typescript/utils/resultTextOptions'
import type { PropType } from 'vue'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

type TPendingLibraryItem = {
  id: string
  systemKey: 'result-units' | 'result-texts'
  text: string
}

export default {
  name: 'CreateAnalysisTestModal',

  props: {
    // When set, this wizard is opened nested inside another wizard (e.g. the
    // Create Analysis wizard's "add new test" step). "Create" then just
    // hands the completed staged form back to the caller and closes, rather
    // than persisting it — the caller decides when/whether it's actually
    // written (see the Create Analysis wizard's atomic batch on its own
    // final "Create").
    onStagedComplete: {
      type: Function as PropType<(form: TAnalysisTestFocusForm) => void>,
      required: false,
      default: undefined,
    },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      currentStep: 1 as number,
      form: buildEmptyAnalysisTestFocusForm(''),
      isLoading: false,
      errorText: '',
      pendingNewItems: [] as TPendingLibraryItem[],
    }
  },

  computed: {
    ...mapState(useTextListStore, ['systemLists']),
    ...mapState(useReportDescriptionStore, ['allReportDescriptions']),
    ...mapState(useAppStore, ['currentLocale']),

    hasValidationErrors(): boolean {
      return (
        (this.form.type === 'value-based' &&
          this.form.colorConfigEnabled &&
          computeValueBasedConditionErrors(this.form.conditions).length > 0) ||
        isSingleSelectQaIncomplete(this.form)
      )
    },

    typeOptions(): { value: string; text: string }[] {
      return [
        {
          value: '',
          text: this.$t('W: Vælg type'),
        },
        {
          value: 'prove-based',
          text: this.$t('area.analysis-test.type.prove-based'),
        },
        {
          value: 'value-based',
          text: this.$t('area.analysis-test.type.value-based'),
        },
        {
          value: 'single-select',
          text: this.$t('area.analysis-test.type.single-select'),
        },
        {
          value: 'multi-select-text',
          text: this.$t('area.analysis-test.type.multi-select-text'),
        },
        {
          value: 'multi-select-number',
          text: this.$t('area.analysis-test.type.multi-select-number'),
        },
      ]
    },

    reportDescriptionOptions(): { value: string; text: string; isDisabled?: boolean }[] {
      return this.buildCollectionOptions(this.form.reportDescriptionIDs)
    },

    suffixReportDescriptionOptions(): { value: string; text: string; isDisabled?: boolean }[] {
      return this.buildCollectionOptions(this.form.suffixReportDescriptionIDs)
    },

    unitOptions(): { value: string; text: string }[] {
      const list = this.systemLists.find((l) => l.systemKey === 'result-units')
      const noUnit = {
        value: '',
        text: this.$t('area.analysis-test.message.no-unit'),
      }
      const pending = this.pendingNewItems
        .filter((x) => x.systemKey === 'result-units')
        .map((x) => ({ value: x.id, text: x.text }))
      if (!list) return [noUnit, ...pending]
      const items = Object.entries(list.items)
        .filter(
          ([id, item]) =>
            item.activeState === 'active' || id === this.form.unitID
        )
        .map(([id, item]) => ({
          value: id,
          text:
            item.text[this.currentLocale] ??
            Object.values(item.text).find((v) => v !== '') ??
            '',
        }))
      return [noUnit, ...items, ...pending]
    },

    selectedUnitText(): string {
      if (!this.form.unitID) return ''
      return (
        this.unitOptions.find((o) => o.value === this.form.unitID)?.text ?? ''
      )
    },

    answerModeOptions(): { value: 'none' | 'optional' | 'required'; text: string }[] {
      return [
        { value: 'none', text: this.$t('W: Ingen svar') },
        { value: 'optional', text: this.$t('W: Valgfrit svar') },
        { value: 'required', text: this.$t('W: Krævet svar') },
      ]
    },

    qaTriggerOptions(): { value: '' | 'always' | 'proved' | 'not-proved'; text: string }[] {
      return [
        { value: '', text: this.$t('W: (ingen)') },
        { value: 'always', text: this.$t('W: Altid') },
        {
          value: 'proved',
          text: this.$t('W: Når svaret er \'{label}\'', { label: this.provedLabel }),
        },
        {
          value: 'not-proved',
          text: this.$t('W: Når svaret er \'{label}\'', { label: this.notProvedLabel }),
        },
      ]
    },

    resultTextItems(): TResultTextOption[] {
      return buildResultTextItems({
        list: this.systemLists.find((l) => l.systemKey === 'result-texts'),
        locale: this.currentLocale,
        selectedIDs: [
          this.form.provedTextID,
          this.form.notProvedTextID,
          ...this.form.conditions.map((c) => c.textID),
        ],
        pendingItems: this.pendingNewItems
          .filter((x) => x.systemKey === 'result-texts')
          .map((x) => ({ value: x.id, text: x.text })),
      })
    },

    provedTextOptions(): TResultTextOption[] {
      return [
        { value: '', text: this.$t('W: {label} (standard)', { label: this.$t('term.yes') }) },
        ...this.resultTextItems,
      ]
    },

    notProvedTextOptions(): TResultTextOption[] {
      return [
        { value: '', text: this.$t('W: {label} (standard)', { label: this.$t('term.no') }) },
        ...this.resultTextItems,
      ]
    },

    provedLabel(): string {
      const selected = this.resultTextItems.find((o) => o.value === this.form.provedTextID)
      return selected?.text || this.$t('term.yes')
    },

    notProvedLabel(): string {
      const selected = this.resultTextItems.find((o) => o.value === this.form.notProvedTextID)
      return selected?.text || this.$t('term.no')
    },
  },

  watch: {
    'form.type'(newType: typeof this.form.type) {
      this.form = {
        ...buildEmptyAnalysisTestFocusForm(newType),
        name: this.form.name,
        displayName: this.form.displayName,
      }
      this.pendingNewItems = []
    },
  },

  methods: {
    // Shared by both Collection pickers on a multi-select test — the subject
    // lists and the suffix lists (MIL-395). Each picker caps at 5
    // independently, so the at-max state is computed from whichever selection
    // is passed in rather than from one shared field.
    buildCollectionOptions(
      selectedIDs: string[]
    ): { value: string; text: string; isDisabled?: boolean }[] {
      // Up to 5 Collections can be pooled per picker — a deliberate product cap,
      // not an example. Once reached, every other option is disabled rather
      // than hidden, so it's clear why they can't be picked.
      const atMax = selectedIDs.length >= 5
      return this.allReportDescriptions
        .filter((rd) => rd.activeState === 'active' || selectedIDs.includes(rd.id))
        .sort((a, b) =>
          a.getName(this.currentLocale).localeCompare(b.getName(this.currentLocale))
        )
        .map((rd) => ({
          value: rd.id,
          text: rd.getName(this.currentLocale),
          isDisabled: atMax && !selectedIDs.includes(rd.id),
        }))
    },

    ...mapActions(useAnalysisTestStore, ['createAnalysisTest']),
    ...mapActions(useModalStore, ['closeTop']),
    ...mapActions(useTextListStore, ['addTextListItem']),

    onPendingCreateUnit(text: string): void {
      const id = generateFirestoreId()
      this.pendingNewItems.push({ id, systemKey: 'result-units', text })
      this.form.unitID = id
    },

    onPendingCreateProvedText(text: string): void {
      const id = generateFirestoreId()
      this.pendingNewItems.push({ id, systemKey: 'result-texts', text })
      this.form.provedTextID = id
    },

    onPendingCreateNotProvedText(text: string): void {
      const id = generateFirestoreId()
      this.pendingNewItems.push({ id, systemKey: 'result-texts', text })
      this.form.notProvedTextID = id
    },

    async onStep1Next(): Promise<void> {
      this.errorText = ''
      this.currentStep = 2
    },

    prevStep(): void {
      this.currentStep = 1
    },

    async onCreate(): Promise<void> {
      this.isLoading = true
      this.errorText = ''

      try {
        // Committed immediately regardless of staged/standalone mode — a
        // shared library item (unit) has no dependency on this particular
        // AnalysisTest ever actually being created, so there's no orphan risk.
        for (const item of this.pendingNewItems) {
          await this.addTextListItem(item.systemKey, item.text, item.id)
        }
        this.pendingNewItems = []

        if (this.onStagedComplete) {
          this.onStagedComplete(this.form)
        } else {
          await this.createAnalysisTest(buildAnalysisTestCreatePayload(this.form))
          useToast().success(this.$t('W: Analysetest oprettet'))
        }
        this.closeTop()
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke oprette analysetest'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
